import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CreatePassV2 from "./CreatePassV2";
import { PASS_VIEW } from "enums";
import { showToastMessage } from "utils";
import { createCardholderV2, getCardholderDetailV2, updateCardholderV2 } from "./api";
import { ImagePlus, LayoutTemplate, ShieldCheck, UserRoundPlus } from "lucide-react";

export default function AdminV2CreateCardholder() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [loading, setLoading] = useState(false);
  const [detailState, setDetailState] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL || "";
  const uploadsBaseUrl = API_URL.replace(/\/api\/?$/, "").replace(/\/$/, "");
  const sourceState = detailState || state;

  useEffect(() => {
    let active = true;

    const hydrateDetail = async () => {
      if (!state?._id) return;

      try {
        const detail = await getCardholderDetailV2(state._id);
        if (active) {
          setDetailState(detail);
        }
      } catch (error) {
        console.error("Failed to hydrate cardholder detail:", error);
      }
    };

    hydrateDetail();

    return () => {
      active = false;
    };
  }, [state?._id]);

  const parsedName = useMemo(() => {
    const full = sourceState?.name?.trim() || "";
    if (!full) return { first: "", last: "" };

    const parts = full.split(" ").filter(Boolean);
    if (parts.length === 1) return { first: parts[0], last: "" };

    return {
      first: parts[0],
      last: parts.slice(1).join(" "),
    };
  }, [sourceState?.name]);

  const initState = useMemo(() => {
    const safeEmails = sourceState?.emails?.length
      ? sourceState.emails
      : sourceState?.email
      ? [sourceState.email]
      : [""];
    const safePhones = sourceState?.phones?.length
      ? sourceState.phones.map((p) => p?.number || p)
      : sourceState?.phone
      ? [sourceState.phone]
      : [""];
    const safeAddresses = sourceState?.addresses?.length
      ? sourceState.addresses.map((a) => ({ value: a?.value || a }))
      : [];

    const stripPreview =
      sourceState?.stripImage && !String(sourceState.stripImage).startsWith("http")
        ? `${uploadsBaseUrl}/uploads/${sourceState.stripImage}`
        : sourceState?.stripImage || "";

    const logoPreview =
      sourceState?.logoImage && !String(sourceState.logoImage).startsWith("http")
        ? `${uploadsBaseUrl}/uploads/${sourceState.logoImage}`
        : sourceState?.logoImage || "";

    return {
      pass_type: sourceState?.passType || PASS_VIEW.APPLE,
      backgroundColor: sourceState?.backgroundColor || "#2C3255",
      prefix: sourceState?.prefix || "",
      firstName: sourceState?.firstName || parsedName.first || "",
      lastName: sourceState?.lastName || parsedName.last || "",
      organisationName: sourceState?.organization || sourceState?.organisationName || "",
      jobTitle: sourceState?.title || sourceState?.jobTitle || "",
      email: safeEmails,
      phone: safePhones,
      companyURL: sourceState?.companyURL || "",
      linkedInURL: sourceState?.linkedInURL || "",
      stripImage: stripPreview,
      logoImage: logoPreview,
      stripImageUrl: stripPreview,
      logoImageUrl: logoPreview,
      stripPreview,
      logoPreview,
      stripImagePreview: stripPreview,
      logoImagePreview: logoPreview,
      stripImageName: "",
      logoImageName: "",
      addresses: safeAddresses,
    };
  }, [sourceState, uploadsBaseUrl, parsedName.first, parsedName.last]);

  const handleSubmit = async (form) => {
    try {
      setLoading(true);

      if (state?._id) {
        const res = await updateCardholderV2(form, state);
        showToastMessage(res?.message || "Cardholder updated successfully");
      } else {
        const res = await createCardholderV2(form);
        showToastMessage(res?.message || "Cardholder created successfully");
      }

      navigate("/admin-v2/cardholders");
    } catch (e) {
      console.error("Create/Update error:", e);
      showToastMessage(e?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tw-space-y-6">
      <section className="admin-v2-panel tw-p-6 lg:tw-p-8">
        <div className="tw-flex tw-flex-col tw-gap-5 lg:tw-flex-row lg:tw-items-end lg:tw-justify-between">
          <div className="tw-max-w-2xl tw-space-y-3">
            <p className="admin-v2-eyebrow">Cardholder editor</p>
            <h1 className="tw-text-3xl tw-font-semibold tw-tracking-tight tw-text-slate-900">
              {state?._id ? "Update Cardholder Pass" : "Create Cardholder Pass"}
            </h1>
            <p className="admin-v2-subtitle">
              Add cardholder details, upload branding assets, and shape the pass.
            </p>
          </div>

          <div className="tw-flex tw-items-center tw-gap-3">
            <button onClick={() => navigate("/admin-v2/cardholders")} className="admin-v2-action admin-v2-action-secondary" type="button">
              Cancel
            </button>
          </div>
        </div>
      </section>

      {/* <section className="admin-v2-soft-panel tw-p-4 lg:tw-p-6">
        <div className="tw-grid tw-gap-4 lg:tw-grid-cols-[minmax(0,1.3fr)_minmax(320px,0.7fr)]">
          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-3">
            <div className="admin-v2-stat-panel">
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <p className="admin-v2-eyebrow">Identity</p>
                <UserRoundPlus size={18} className="tw-text-[var(--admin-v2-accent)]" />
              </div>
              <p className="tw-mt-4 tw-text-lg tw-font-semibold admin-v2-text-strong">
                {state?._id ? "Editing existing cardholder" : "Creating a new cardholder"}
              </p>
              <p className="tw-mt-2 tw-text-sm admin-v2-text-muted">
                Keep names, job title, and workspace details accurate before saving.
              </p>
            </div>

            <div className="admin-v2-stat-panel">
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <p className="admin-v2-eyebrow">Branding</p>
                <ImagePlus size={18} className="tw-text-[var(--admin-v2-accent)]" />
              </div>
              <p className="tw-mt-4 tw-text-lg tw-font-semibold admin-v2-text-strong">Legacy editor preserved</p>
              <p className="tw-mt-2 tw-text-sm admin-v2-text-muted">
                Crop, upload, and pass preview behavior stays aligned with the old create cardholder flow.
              </p>
            </div>

            <div className="admin-v2-stat-panel">
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <p className="admin-v2-eyebrow">Readiness</p>
                <ShieldCheck size={18} className="tw-text-[var(--admin-v2-accent)]" />
              </div>
              <p className="tw-mt-4 tw-text-lg tw-font-semibold admin-v2-text-strong">
                {state?._id ? "Update and republish" : "Complete and publish"}
              </p>
              <p className="tw-mt-2 tw-text-sm admin-v2-text-muted">
                Required fields, images, and contact details should be complete before submission.
              </p>
            </div>
          </div>

          <div className="admin-v2-brand-band tw-rounded-[24px] tw-p-5">
            <div className="tw-flex tw-items-center tw-gap-2">
              <LayoutTemplate size={17} className="tw-text-[var(--admin-v2-accent)]" />
              <p className="admin-v2-eyebrow">Editor notes</p>
            </div>
            <div className="tw-mt-4 tw-space-y-4">
              <div>
                <p className="tw-text-sm tw-font-semibold admin-v2-text-strong">Old behavior preserved</p>
                <p className="tw-mt-1 tw-text-sm tw-leading-6 admin-v2-text-muted">
                  Image upload, crop flow, and the existing pass editor stay intact. We only keep the screen cleaner from the top.
                </p>
              </div>
              <div>
                <p className="tw-text-sm tw-font-semibold admin-v2-text-strong">Best input order</p>
                <p className="tw-mt-1 tw-text-sm tw-leading-6 admin-v2-text-muted">
                  Set color first, then logo and strip, then name, title, company, and contact details.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      <section className="admin-v2-soft-panel tw-p-4 lg:tw-p-6">
        <CreatePassV2
          key={state?._id || "create"}
          initState={initState}
          onSubmit={handleSubmit}
          btnLabel={loading ? "Please wait..." : state?._id ? "Update Cardholder" : "Create Cardholder"}
          showDeviceType={false}
          logoImageInput
          stripImageInput
          disabled={loading}
        />
      </section>
    </div>
  );
}
